import NextImage from 'next/image'
import { useCallback, useContext, useMemo } from 'react'

import { getItemTitle, toRecord } from '../../../lib/helpers'
import { getBasicImageLoader, getSanityImageUrl } from '../../../lib/image'
import { getIsTextureValid } from '../../../lib/validation/state-is-valid'
import {
  type FrameBuilderProfile,
  type FrameBuilderTexture,
} from '../../../lib/state'
import {
  getStatePrice,
  getStatePriceWithDiscount,
} from '../../../lib/state-price'
import { FrameBuilderConfigurationContext } from '../../../contexts/configuration-context'
import {
  FrameBuilderCommittedStateContext,
  FrameBuilderSelectedStateContext,
} from '../../../contexts/state-context'
import { FrameBuilderStringsContext } from '../../../contexts/strings-context'
import { FrameBuilderValidationContext } from '../../../contexts/validation-context'

import ItemList from '../../utility/item-list'
import PriceBadge from '../../utility/price-badge'
import ProfileDetails from './profile-details'

const ProfileList = () => {
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { lastValidState, enabledItems } = useContext(
    FrameBuilderValidationContext,
  )
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const configuration = useContext(FrameBuilderConfigurationContext)

  const profileTexturePresets = useMemo(
    () =>
      toRecord<FrameBuilderTexture | undefined>(
        configuration.profiles.map((profile) => {
          const selectedProfile =
            selectedState.profile ?? lastValidState.profile
          const selectedTexture = selectedState.texture
          const defaultTexture = configuration.textures.find(
            (texture) => texture._id === profile.defaultTexture?._id,
          )

          return {
            [profile._id]:
              profile._id === selectedProfile?._id ||
              getIsTextureValid(profile, selectedTexture) ||
              !defaultTexture
                ? selectedTexture
                : defaultTexture,
          }
        }),
      ),
    [
      lastValidState.profile,
      configuration.profiles,
      configuration.textures,
      selectedState.profile,
      selectedState.texture,
    ],
  )

  // Calculate frame prices for comparison
  const framePrices = useMemo(
    () =>
      toRecord<number>(
        configuration.profiles.map((profile) => ({
          [profile._id]: getStatePrice(
            {
              ...committedState,
              profile,
              texture: profileTexturePresets[profile._id],
            },
            configuration,
          ),
        })),
      ),
    [committedState, configuration, profileTexturePresets],
  )
  const framePricesWithDiscount = useMemo(
    () =>
      toRecord<number>(
        configuration.profiles.map((profile) => ({
          [profile._id]: getStatePriceWithDiscount(
            {
              ...committedState,
              profile,
              texture: profileTexturePresets[profile._id],
            },
            configuration,
          ),
        })),
      ),
    [committedState, configuration, profileTexturePresets],
  )

  const selectProfile = useCallback(
    (preset: FrameBuilderProfile) => {
      dispatchSelectedState({
        type: 'frameStyle',
        profile: preset,
      })
    },
    [dispatchSelectedState],
  )

  const handleChange = useCallback(
    (id: string) => {
      const newProfile = configuration.profiles.find(
        (profile) => profile._id === id,
      )

      if (newProfile) {
        selectProfile(newProfile)
      }
    },
    [configuration.profiles, selectProfile],
  )

  return (
    <ItemList
      defaultValue={selectedState.profile?._id}
      onChange={handleChange}
      items={configuration.profiles.map((profile) => {
        const textureProfileReference = profileTexturePresets[
          profile._id
        ]?.availableFor?.find(
          (linkedProfile) => linkedProfile.profile._ref === profile?._id,
        )
        const isEnabled = enabledItems.profileIds.includes(profile._id)

        const profileTexturePreset = profileTexturePresets[profile._id]
        const profileTexturePresetImage =
          profileTexturePreset?.icon ?? profileTexturePreset?.image
        const profileTexturePresetImageUrl = getSanityImageUrl(
          profileTexturePresetImage,
          {
            width: 24,
            height: 24,
          },
        )

        return {
          id: profile._id,
          title: getItemTitle(
            frameBuilderStrings,
            profile.title,
            isEnabled ? textureProfileReference?.discountPercent : 0,
          ),
          image: profile.icon,
          description: (
            <>
              {profileTexturePreset &&
                profileTexturePresetImage &&
                profileTexturePresetImageUrl && (
                  <NextImage
                    src={profileTexturePresetImageUrl}
                    width={
                      profileTexturePresetImage.asset?.metadata?.dimensions
                        ?.width
                    }
                    height={
                      profileTexturePresetImage.asset?.metadata?.dimensions
                        ?.height
                    }
                    alt={profileTexturePreset.title ?? ''}
                    loader={getBasicImageLoader}
                    unoptimized
                    className="w-[24px] h-[24px] border"
                  />
                )}

              {selectedState.profile &&
                selectedState.profile._id !== profile._id &&
                isEnabled && (
                  <>
                    <PriceBadge
                      price={
                        framePricesWithDiscount[profile._id] -
                        framePricesWithDiscount[selectedState.profile._id]
                      }
                      showPlusSign={true}
                      className="ml-1"
                    />
                    {textureProfileReference?.discountPercent && (
                      <PriceBadge
                        price={
                          framePrices[profile._id] -
                          framePricesWithDiscount[selectedState.profile._id]
                        }
                        showPlusSign={true}
                        disabled
                        className="ml-1"
                      />
                    )}
                  </>
                )}
            </>
          ),
          disabled: !isEnabled,
          error: lastValidState.profile?._id === profile._id,
          details: <ProfileDetails profileId={profile._id} />,
        }
      })}
    />
  )
}

export default ProfileList
